import React, { useCallback, useEffect, useState } from 'react';

import { Tooltip } from '@primer/react';

import ConfirmationDialog from '../../../common/ConfirmationDialog/ConfirmationDialog';
import InfoCircleIcon from '../../../common/icons/InfoCircleIcon';
import Label from '../../../common/Labels/Label';
import PasswordInput from '../../../common/TextInputs/PasswordInput/PasswordInput';
import RadioGroup from '../../../common/RadioGroup/RadioGroup';

import { getThemeColor } from '../../../../utils/theme-utils';
import ContactSupport from '../../../common/Labels/ContactSupportLink';

const PASSWORD_GROUPS = [
  { id: 'temporary', label: 'Temporary' },
  { id: 'permanent', label: 'Permanent' },
];

export const ResetPasswordDialog = ({
  error = null,
  userId = {},
  onPasswordReset = () => {},
  showDialog = false,
  onDialogToggle = () => {},
}) => {
  const [password, setPassword] = useState('');
  const [isTemporary, setTemporary] = useState(true);

  const passwordError = !password?.length ? 'Invalid password' : null;
  const onEntryValidation = entry => entry?.length;

  const onPasswordGroupSelect = (selectedType) => {
    setTemporary(selectedType === 'temporary');
  }

  const handleConfirm = useCallback(() => {
    onPasswordReset(userId, password, isTemporary);
  }, [onPasswordReset, userId, password, isTemporary])

  return (
    <ConfirmationDialog
      title="Reset Password"
      confirmButtonText="Reset"
      confirmButtonType="danger"
      error={error}
      showDialog={showDialog}
      onDialogToggle={onDialogToggle}
      modalAnimationKey="reset-password-modal"
      onConfirm={handleConfirm}
      disableConfirmationButton={passwordError}
    >
      <div className="w-filter-modal flex flex-col p-2 m-1">
        <Label>
          The password set here needs to be sent to the user.  If it is a temporary password, they will be prompted to change their password on next login.
        </Label>
        <div className="m-1 grid grid-cols-2">
          <div className="flex flex-col items-start justify-start">
            <div className="flex items-center justify-start">
              <Label bold className="m-1">Password</Label>
              <Tooltip
                aria-label="The password here needs to be sent to the user. If it is a temporary password, they will be prompted to change their password on next login."
                direction="ne"
                wrap
              >
                <InfoCircleIcon
                  size={4}
                  fill={getThemeColor('brand')}
                  color={getThemeColor('background')}
                  className="ml-1"
                />
              </Tooltip>
            </div>
            <PasswordInput
              value={password}
              onChange={setPassword}
              onEntryValidation={onEntryValidation}
              errorMessage={passwordError}
              block
            />
          </div>
          <div className="flex flex-col items-start m-1">
            <RadioGroup
              className="mt-8"
              id="password-mode-selection"
              selectedItemId={isTemporary ? 'temporary' : 'permanent'}
              items={PASSWORD_GROUPS}
              onSelect={onPasswordGroupSelect}
              renderAsRow
            />
          </div>
        </div>
        {
          error && (
            <div className="mx-2">
              <Label color="error" size="sm">
                Error while resetting password. Please try again or <ContactSupport>contact support</ContactSupport> if the issue persists.
              </Label>
            </div>
          )
        }
      </div>
    </ConfirmationDialog>
  )
};

export default ResetPasswordDialog;
